import { Link, router, usePage } from '@inertiajs/react'
import { useEffect, useRef, type ReactNode } from 'react'
import logo from '@images/logo_bk_mini.png'
import { AppLink } from '@/components/AppLink'
import { t } from '@/lib/i18n'
import { FlashMessages } from '@/layouts/FlashMessages'
import { MetaTags } from '@/layouts/MetaTags'
import { footerLinks, isCurrent, memberMenu, navigation } from '@/layouts/navigation'
import { root_path } from '@/routes'

/** 公開サイト共通のレイアウト（ナビ・フラッシュ・フッター・スマートフォンの会員メニュー）。ページを移動しても保持される */
export function SiteLayout({ children }: { children: ReactNode }) {
  const { url, props } = usePage()
  const { region, auth } = props
  const user = auth.user
  const nav = navigation(region, Boolean(user))
  const collapse = useRef<HTMLDivElement>(null)

  // SPA 遷移ではページが再読み込みされないので、スマートフォンのメニューを閉じる
  useEffect(
    () =>
      router.on('navigate', () => {
        collapse.current?.classList.remove('show')
      }),
    [],
  )

  return (
    <>
      <MetaTags />
      <div className="container">
        <nav className="shadow navbar navbar-expand-lg navbar-light fixed-top bg-white">
          <Link className="navbar-brand" href={root_path()}>
            <img src={logo} className="logo-nav" />
          </Link>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNavAltMarkup"
            aria-controls="navbarNavAltMarkup"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarNavAltMarkup" ref={collapse}>
            <div className="navbar-nav me-auto"></div>
            <div className="navbar-nav">
              <li className="nav-item dropdown">
                <a
                  className={`nav-link dropdown-toggle ${isCurrent(url, nav.dropdown.activeWhen) ? 'active' : ''}`}
                  href="#"
                  id="navbarDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  {nav.dropdown.label}
                </a>
                <div className="dropdown-menu" aria-labelledby="navbarDropdown">
                  {nav.dropdown.items.map((link) => (
                    <AppLink key={link.href} link={link} className="dropdown-item" />
                  ))}
                </div>
              </li>
              {nav.links.map((link) => (
                <AppLink
                  key={link.href}
                  link={link}
                  className={`nav-item nav-link ${isCurrent(url, link.activeWhen) ? 'active' : ''}`}
                />
              ))}
            </div>
          </div>
        </nav>
      </div>

      <FlashMessages />

      {children}

      <footer className="text-muted bg-footer">
        <div className="container">
          <div className="row">
            <div className="col-md text-end">
              {region === 'jp' ? (
                <div className="footer-terms">
                  <span className="footer-title">SUNSET CELLARS</span>
                  <nav>
                    <ul className="footer-links">
                      {footerLinks(region, Boolean(user)).map((link) => (
                        <li key={link.href}>
                          <AppLink link={link} />
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              ) : (
                <p className="footer-terms">
                  &copy; {t('layouts.application.title')}
                  <br />
                </p>
              )}
            </div>
          </div>
        </div>
      </footer>

      {user?.payment_registered && (
        <div className="bottom-menu d-md-none">
          <div className="container-fluid">
            <div className="row">
              {memberMenu(region).map((item) => (
                <div key={item.href} className={`col ${isCurrent(url, item.activeWhen) ? 'bg-selected' : ''}`}>
                  <Link className="menu-btn" href={item.href}>
                    <div className="menu-label">
                      <i className={item.icon}></i>
                      <p>{item.short}</p>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
